// Maps to backend/routers/shared_resources.py (conceptual mapping as fetch is direct)
import { useState, useCallback } from 'react';
import { sharedApi } from '../api/shared';
import { env } from '../utils/env';

interface OptimisticMember {
  id: string;
  email: string;
  status: 'pending';
  joined_at: string;
}

interface OptimisticUpdate {
  member: OptimisticMember;
  rollback: () => void;
}

export const useOptimisticShare = (
  type: 'account' | 'budget',
  resourceId: string,
  onUpdate?: () => void
) => {
  const [optimisticMembers, setOptimisticMembers] = useState<OptimisticMember[]>([]);
  const [pendingUpdates, setPendingUpdates] = useState<OptimisticUpdate[]>([]);

  const shareResource = useCallback(async (email: string) => {
    // Create optimistic member
    const optimisticMember: OptimisticMember = {
      id: `temp-${Date.now()}`,
      email,
      status: 'pending',
      joined_at: new Date().toISOString(),
    };

    // Create rollback function
    const rollback = () => {
      setOptimisticMembers(prev =>
        prev.filter(m => m.id !== optimisticMember.id)
      );
      setPendingUpdates(prev =>
        prev.filter(u => u.member.id !== optimisticMember.id)
      );
    };

    // Add to optimistic state
    setOptimisticMembers(prev => [...prev, optimisticMember]);
    setPendingUpdates(prev => [...prev, { member: optimisticMember, rollback }]);

    try {
      // Make API call
      let shareUrl = '';
      if (type === 'account') {
        // Presuming the original sharedApi.shareAccount correctly targets /api/shared/accounts/{resourceId}/share
        // For robustness and directness, constructing the call here.
        shareUrl = `${env.API_URL}/api/shared/accounts/${resourceId}/share`;
      } else { // type === 'budget'
        shareUrl = `${env.API_URL}/api/shared/budgets/categories/${resourceId}/share`;
      }

      const response = await fetch(shareUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Authorization headers should be handled by a global fetch or passed if needed
        },
        credentials: 'include',
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({ detail: 'Failed to share resource' }));
        throw new Error(data.detail || 'Failed to share resource');
      }

      // Clear optimistic state on success
      setOptimisticMembers(prev =>
        prev.filter(m => m.id !== optimisticMember.id)
      );
      setPendingUpdates(prev =>
        prev.filter(u => u.member.id !== optimisticMember.id)
      );

      // Refresh actual data
      onUpdate?.();

    } catch (error) {
      // Rollback optimistic update on error
      rollback();
      throw error;
    }
  }, [type, resourceId, onUpdate]);

  const clearPendingUpdates = useCallback(() => {
    // Rollback all pending updates
    pendingUpdates.forEach(update => update.rollback());
    setPendingUpdates([]);
  }, [pendingUpdates]);

  return {
    optimisticMembers,
    shareResource,
    clearPendingUpdates,
    hasPendingUpdates: pendingUpdates.length > 0
  };
}; 